// src/pages/layout/layout.js
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Layout, Menu, Button, Drawer, Avatar, Dropdown, Typography, message
} from 'antd';
import {
  HomeOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  TeamOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TagsOutlined,
  UserSwitchOutlined,
  FileTextOutlined,
  DollarOutlined,
  BarChartOutlined,
  HistoryOutlined,
  SettingOutlined,
  FormOutlined,
  SoundOutlined,
  CheckCircleOutlined,
  ShopOutlined,
  DollarCircleOutlined,
  BankOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  WalletOutlined,
  DatabaseOutlined,
  MinusCircleOutlined,
  AppstoreOutlined,
  CreditCardOutlined,
  MoneyCollectOutlined,
  FileDoneOutlined,
  SafetyOutlined,
  LineChartOutlined,
  ScheduleOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { LoginContext } from '../../context/loginContext';
import { usePermissions } from '../../context/permissionsContext';
import { useModulos } from '../../context/modulosContext';
import { apiClient } from '../../config/apiClient';
import playmanagerLogo from '../../assets/PlayManager.png';

const { Header, Content, Sider } = Layout;
const { Text } = Typography;
const palette = {
  bgBase: '#151515',
  bgSurface: '#202020',
  textPrimary: '#f5f5f5',
  border: '#343434',
};

const MainLayout = ({ children }) => {
  const { user, logout } = useContext(LoginContext);
  const { hasPermission, getFirstGestionPath, loading: permissionsLoading } = usePermissions();
  const { modulosActivos, modulosLoaded } = useModulos();
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved !== null ? saved === 'true' : true;
  });
  const sidebarHoverTimer = useRef(null);
  const clubDisabledWarningPathRef = useRef(null);
  const [moduloSeleccionado, setModuloSeleccionado] = useState(() => {
    return localStorage.getItem('moduloSeleccionado') || 'inicio';
  });
  const isAdmin =
    user?.tiu_descripcion?.toUpperCase() === 'ADMINISTRADOR' ||
    user?.usu_tipousuario === 1;
  const roleToCheck = (() => {
    const role = (user?.tiu_descripcion || '').toUpperCase();
    if (role) return role;
    if (user?.usu_tipousuario === 1) return 'ADMINISTRADOR';
    if (user?.usu_tipousuario === 3) return 'JUGADOR';
    if (user?.usu_tipousuario) return 'ALUMNO';
    return '';
  })();

  const getModuleByPath = (path) => {
    if (!path) return null;
    // Categorías de alumnos: compartidas; la ruta no pertenece solo a academia (validación aparte)
    if (path === '/administracion-categorias') return null;
    if (path === '/datos-academia') return 'academia';
    if (path === '/gestion' || path.startsWith('/gestion/')) return 'gestion';
    if (path === '/club' || path.startsWith('/club/')) return 'club';
    if (path === '/torneos' || path.startsWith('/torneos/')) return 'torneos';
    if (path === '/jugador/mis-torneos' || path.startsWith('/jugador/mis-torneos/')) return 'torneos';
    if (path.startsWith('/jugador/')) return 'club';
    if (path === '/reservar-cancha' || path === '/mis-turnos-cancha') return 'club';
    if (
      path.startsWith('/administracion-') ||
      path.startsWith('/agenda-') ||
      path.startsWith('/dashboard') ||
      path.startsWith('/solicitudes-') ||
      path.startsWith('/registro-pagos') ||
      path.startsWith('/descuento-') ||
      path.startsWith('/configuracion-') ||
      path.startsWith('/asistencia-')
    ) {
      return 'academia';
    }
    return null;
  };

  const isModuleEnabled = (modulo) => {
    if (!modulo) return true;
    const enabledByConfig = Boolean(modulosActivos[modulo]);
    if (!enabledByConfig) return false;

    // Mientras cargan permisos, no bloquear visualmente para evitar parpadeos.
    if (isAdmin && permissionsLoading) {
      return enabledByConfig;
    }

    // Para administradores, Club y Academia también dependen del permiso por rol.
    if (isAdmin) {
      if (modulo === 'club') {
        return hasPermission('modulos', 'ver_club');
      }
      if (modulo === 'academia') {
        return hasPermission('modulos', 'ver_academia');
      }
    }

    return true;
  };

  // Sincronizar módulo cuando cambia la ruta (/datos-club no cambia el módulo)
  useEffect(() => {
    const path = location.pathname;
    if (path === '/datos-club') return;
    if (path === '/datos-academia') {
      const modulo = localStorage.getItem('moduloSeleccionado');
      if (modulo !== 'academia') {
        localStorage.setItem('moduloSeleccionado', 'academia');
        setModuloSeleccionado('academia');
      }
      return;
    }
    if (path.startsWith('/club/')) {
      const modulo = localStorage.getItem('moduloSeleccionado');
      if (modulo !== 'club') {
        localStorage.setItem('moduloSeleccionado', 'club');
        setModuloSeleccionado('club');
      }
    } else if (path.startsWith('/gestion/')) {
      const modulo = localStorage.getItem('moduloSeleccionado');
      if (modulo !== 'gestion') {
        localStorage.setItem('moduloSeleccionado', 'gestion');
        setModuloSeleccionado('gestion');
      }
    } else if (path === '/torneos' || path.startsWith('/torneos/')) {
      const modulo = localStorage.getItem('moduloSeleccionado');
      if (modulo !== 'torneos') {
        localStorage.setItem('moduloSeleccionado', 'torneos');
        setModuloSeleccionado('torneos');
      }
    } else if (path === '/home-administrador') {
      const modulo = localStorage.getItem('moduloSeleccionado');
      if (modulo !== 'inicio') {
        localStorage.setItem('moduloSeleccionado', 'inicio');
        setModuloSeleccionado('inicio');
      }
    } else if (path === '/administracion-categorias') {
      const modulo = localStorage.getItem('moduloSeleccionado');
      if (modulo === 'club' || modulo === 'academia') {
        return;
      }
      if (modulosLoaded && modulosActivos.club) {
        localStorage.setItem('moduloSeleccionado', 'club');
        setModuloSeleccionado('club');
      } else if (modulosLoaded && modulosActivos.academia) {
        localStorage.setItem('moduloSeleccionado', 'academia');
        setModuloSeleccionado('academia');
      }
    } else if (path.startsWith('/administracion-') || path.startsWith('/agenda-') || path.startsWith('/dashboard') || path.startsWith('/solicitudes-') || path.startsWith('/registro-pagos') || path.startsWith('/descuento-') || path.startsWith('/configuracion-') || path.startsWith('/asistencia-')) {
      const modulo = localStorage.getItem('moduloSeleccionado');
      if (modulo !== 'academia') {
        localStorage.setItem('moduloSeleccionado', 'academia');
        setModuloSeleccionado('academia');
      }
    }
  }, [location.pathname, modulosLoaded, modulosActivos]);

  useEffect(() => {
    if (!isAdmin || !modulosLoaded || permissionsLoading) return;
    const path = location.pathname;
    if (path === '/administracion-categorias') {
      if (!modulosActivos.club && !modulosActivos.academia) {
        localStorage.setItem('moduloSeleccionado', 'inicio');
        setModuloSeleccionado('inicio');
        message.warning('Se requiere el módulo Club o Academia para administrar categorías.');
        navigate('/home-administrador', { replace: true });
      }
      return;
    }
    const moduloRuta = getModuleByPath(location.pathname);
    if (moduloRuta && !isModuleEnabled(moduloRuta)) {
      localStorage.setItem('moduloSeleccionado', 'inicio');
      setModuloSeleccionado('inicio');
      message.warning(`El módulo ${moduloRuta} está deshabilitado.`);
      navigate('/home-administrador', { replace: true });
    }
  }, [isAdmin, modulosLoaded, permissionsLoading, modulosActivos, location.pathname, navigate]);

  useEffect(() => {
    if (!modulosLoaded || isAdmin) return;
    if (isModuleEnabled('club')) return;

    const path = location.pathname;
    const enRutaJugadorClub = path === '/jugador/nuevo-turno' || path === '/jugador/mis-turnos';
    const enRutaJugadorTorneos = path === '/jugador/mis-torneos' || path.startsWith('/jugador/mis-torneos/');
    const enRutaAlumnoClub = path === '/reservar-cancha' || path === '/mis-turnos-cancha';

    if (roleToCheck === 'JUGADOR' && enRutaJugadorClub) {
      if (clubDisabledWarningPathRef.current !== path) {
        message.warning('El módulo club está deshabilitado.');
        clubDisabledWarningPathRef.current = path;
      }
      navigate('/jugador/mis-datos', { replace: true });
      return;
    }

    if (roleToCheck === 'JUGADOR' && enRutaJugadorTorneos && !isModuleEnabled('torneos')) {
      if (clubDisabledWarningPathRef.current !== path) {
        message.warning('El módulo torneos está deshabilitado.');
        clubDisabledWarningPathRef.current = path;
      }
      navigate('/jugador/mis-datos', { replace: true });
      return;
    }

    if (roleToCheck === 'ALUMNO' && enRutaAlumnoClub) {
      if (clubDisabledWarningPathRef.current !== path) {
        message.warning('El módulo club está deshabilitado.');
        clubDisabledWarningPathRef.current = path;
      }
      navigate('/home-alumno', { replace: true });
    }
  }, [isAdmin, modulosLoaded, modulosActivos.club, location.pathname, navigate, roleToCheck]);

  useEffect(() => {
    if (isAdmin) return;
    setSidebarCollapsed(false);
  }, [isAdmin]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAvatar = async () => {
      if (!user) {
        if (isMounted) setAvatarSrc(null);
        return;
      }

      const role =
        (user?.tiu_descripcion || '').toUpperCase() ||
        (user?.usu_tipousuario === 2 ? 'ALUMNO' : user?.usu_tipousuario === 3 ? 'JUGADOR' : '');

      if (role !== 'ALUMNO' && role !== 'JUGADOR') {
        if (isMounted) setAvatarSrc(null);
        return;
      }

      try {
        const endpoint = role === 'ALUMNO' ? '/mi/perfil' : '/club/mi/perfil';
        const response = await apiClient.get(endpoint);
        if (!response.ok) {
          if (isMounted) setAvatarSrc(null);
          return;
        }
        const data = await response.json();
        const src = role === 'ALUMNO' ? data?.alu_foto : data?.jcl_foto;
        if (isMounted) setAvatarSrc(src || null);
      } catch (error) {
        if (isMounted) setAvatarSrc(null);
      }
    };

    loadAvatar();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
    // Con HashRouter, redirigir a portada
    window.location.href = '/#/';
  };

  const getPrimeraRutaGestionConPermiso = () => {
    const path = typeof getFirstGestionPath === 'function' ? getFirstGestionPath() : null;
    return path || '/gestion/dashboard';
  };

  const handleSelectModulo = (modulo) => {
    if (!isModuleEnabled(modulo)) {
      message.warning(`El módulo ${modulo} está deshabilitado.`);
      return;
    }
    localStorage.setItem('moduloSeleccionado', modulo);
    setModuloSeleccionado(modulo);
    if (modulo === 'academia') {
      navigate('/dashboard');
    } else if (modulo === 'gestion') {
      navigate('/gestion');
    } else if (modulo === 'club') {
      navigate('/club');
    } else if (modulo === 'torneos') {
      navigate('/torneos');
    }
  };

  // Función para obtener items del menú según el rol
  const getMenuItems = () => {
    switch (roleToCheck) {
      case 'ADMINISTRADOR': {
        const modulosMenuItem = {
          key: '/home-administrador',
          icon: <AppstoreOutlined />,
          label: <Link to="/home-administrador">Módulos</Link>,
        };
        const datosClubMenuItem = {
          key: '/datos-club',
          icon: <BankOutlined />,
          label: <Link to="/datos-club">Datos del club</Link>,
        };
        const datosAcademiaMenuItem = {
          key: '/datos-academia',
          icon: <BankOutlined />,
          label: <Link to="/datos-academia">Datos de academia</Link>,
          disabled: !isModuleEnabled('academia'),
        };
        const menuAccesosDatos = [modulosMenuItem, datosClubMenuItem, datosAcademiaMenuItem];

        if (moduloSeleccionado === 'inicio') {
          return menuAccesosDatos;
        }

        // Si el módulo seleccionado es 'club', mostrar menú del club
        if (moduloSeleccionado === 'club') {
          return [
            {
              key: '/club',
              icon: <HomeOutlined />,
              label: <Link to="/club">Inicio</Link>,
            },
            {
              key: '/club/dashboard',
              icon: <BarChartOutlined />,
              label: <Link to="/club/dashboard">Dashboard</Link>,
            },
            {
              key: '/club/canchas',
              icon: <EnvironmentOutlined />,
              label: <Link to="/club/canchas">Canchas</Link>,
            },
            {
              key: '/club/precios',
              icon: <DollarCircleOutlined />,
              label: <Link to="/club/precios">Precios</Link>,
            },
            {
              key: '/club/turnos',
              icon: <CalendarOutlined />,
              label: <Link to="/club/turnos">Turnos</Link>,
            },
            {
              key: '/club/jugadores',
              icon: <TeamOutlined />,
              label: <Link to="/club/jugadores">Jugadores</Link>,
            },
            {
              key: '/club/difusiones',
              icon: <SoundOutlined />,
              label: <Link to="/club/difusiones">Difusiones</Link>,
            },
            {
              key: '/club/asistencia-turnos',
              icon: <CheckCircleOutlined />,
              label: <Link to="/club/asistencia-turnos">Confirmación Turnos</Link>,
            },
            {
              key: 'configuracion-club-submenu',
              icon: <SettingOutlined />,
              label: 'Configuración',
              children: [
                {
                  key: '/administracion-categorias',
                  icon: <TagsOutlined />,
                  label: <Link to="/administracion-categorias">Categorías</Link>,
                },
                {
                  key: '/club/senia',
                  icon: <DollarOutlined />,
                  label: <Link to="/club/senia">Seña</Link>,
                },
              ],
            },
            modulosMenuItem,
          ];
        }
        if (moduloSeleccionado === 'torneos') {
          return [
            {
              key: '/torneos',
              icon: <HomeOutlined />,
              label: <Link to="/torneos">Inicio</Link>,
            },
            {
              key: '/torneos/torneos',
              icon: <TrophyOutlined />,
              label: <Link to="/torneos/torneos">Torneos</Link>,
            },
            modulosMenuItem,
          ];
        }
        // Si el módulo seleccionado es 'gestion', mostrar menú de gestión (filtrado por permisos)
        if (moduloSeleccionado === 'gestion') {
          const items = [
            { key: '/gestion', icon: <HomeOutlined />, label: <Link to="/gestion">Inicio</Link> },
            { permission: ['dashboard', 'ver'], key: '/gestion/dashboard', icon: <BarChartOutlined />, label: <Link to="/gestion/dashboard">Dashboard</Link> },
            { permission: ['compras', 'ver'], key: '/gestion/compras', icon: <ShoppingCartOutlined />, label: <Link to="/gestion/compras">Compras</Link> },
            { permission: ['ventas', 'ver'], key: '/gestion/ventas', icon: <DollarOutlined />, label: <Link to="/gestion/ventas">Ventas</Link> },
            { permission: ['gastos', 'ver'], key: '/gestion/gastos', icon: <MinusCircleOutlined />, label: <Link to="/gestion/gastos">Gastos</Link> },
            { permission: ['cobros', 'ver'], key: '/gestion/cobros', icon: <MoneyCollectOutlined />, label: <Link to="/gestion/cobros">Cobros</Link> },
            { permission: ['cuenta_corriente', 'ver'], key: '/gestion/cuenta-corriente', icon: <CreditCardOutlined />, label: <Link to="/gestion/cuenta-corriente">Ctacte</Link> },
            { permission: ['caja_diaria', 'ver'], key: '/gestion/caja-diaria', icon: <WalletOutlined />, label: <Link to="/gestion/caja-diaria">Caja Diaria</Link> },
            { key: '/gestion/agenda', icon: <CalendarOutlined />, label: <Link to="/gestion/agenda">Agenda</Link> },
            { permission: ['rendicion_caja', 'ver'], key: '/gestion/rendicion-caja', icon: <FileDoneOutlined />, label: <Link to="/gestion/rendicion-caja">Rendición de caja</Link> },
            { permission: ['articulos', 'ver'], key: '/gestion/movimientos-stock', icon: <DatabaseOutlined />, label: <Link to="/gestion/movimientos-stock">Mov Stock</Link> },
            { permission: ['sueldos', 'ver'], key: '/gestion/sueldos', icon: <DollarOutlined />, label: <Link to="/gestion/sueldos">Sueldos</Link> },
            {
              permission: ['usuarios', 'ver'],
              key: 'gestion-permisos-submenu',
              icon: <SafetyOutlined />,
              label: 'Permisos',
              children: [
                { permission: ['usuarios', 'ver'], key: '/gestion/usuarios', icon: <UserOutlined />, label: <Link to="/gestion/usuarios">Usuarios</Link> },
                { permission: ['usuarios', 'ver'], key: '/gestion/roles', icon: <TeamOutlined />, label: <Link to="/gestion/roles">Roles</Link> },
              ],
            },
            {
              permission: ['articulos', 'ver'],
              key: 'gestion-ajustes-submenu',
              icon: <SettingOutlined />,
              label: 'Ajustes',
              children: [
                { permission: ['articulos', 'ver'], key: '/gestion/articulos', icon: <ShoppingOutlined />, label: <Link to="/gestion/articulos">Artículos</Link> },
                { permission: ['articulos', 'ver'], key: '/gestion/ganancias', icon: <LineChartOutlined />, label: <Link to="/gestion/ganancias">Ganancias</Link> },
                { permission: ['categorias_articulos', 'ver'], key: '/gestion/categorias', icon: <TagsOutlined />, label: <Link to="/gestion/categorias">Categorías de Artículos</Link> },
                { permission: ['proveedores', 'ver'], key: '/gestion/proveedores', icon: <ShopOutlined />, label: <Link to="/gestion/proveedores">Proveedores</Link> },
                { permission: ['formas_pago', 'ver'], key: '/gestion/formas-pago', icon: <DollarCircleOutlined />, label: <Link to="/gestion/formas-pago">Formas de Pago</Link> },
                { permission: ['mesas', 'ver'], key: '/gestion/mesas', icon: <AppstoreOutlined />, label: <Link to="/gestion/mesas">Mesas</Link> },
                { permission: ['empleados', 'ver'], key: '/gestion/empleados', icon: <TeamOutlined />, label: <Link to="/gestion/empleados">Empleados</Link> },
                { key: '/gestion/configuracion', icon: <SettingOutlined />, label: <Link to="/gestion/configuracion">Configuración</Link> },
              ],
            },
          ];
          const canViewItem = (item) => {
            if (!item.permission) return true;
            return hasPermission(item.permission[0], item.permission[1]);
          };
          const withUsuariosSubmenu = items.map((item) => {
            if (item.children) {
              const filteredChildren = item.children.filter((c) => canViewItem(c));
              if (filteredChildren.length === 0) return null;
              return { ...item, children: filteredChildren };
            }
            return canViewItem(item) ? item : null;
          });
          return [...withUsuariosSubmenu.filter(Boolean), modulosMenuItem];
        }
        // Si el módulo seleccionado es 'academia', mostrar menú academia (Dashboard primero, Configuración al final)
        return [
          {
            key: '/dashboard',
            icon: <BarChartOutlined />,
            label: <Link to="/dashboard">Dashboard</Link>,
          },
          {
            key: '/agenda-calendario',
            icon: <CalendarOutlined />,
            label: <Link to="/agenda-calendario">Agenda</Link>,
          },
          {
            key: 'alumnos-submenu',
            icon: <TeamOutlined />,
            label: 'Alumnos',
            children: [
              {
                key: '/administracion-alumnos',
                icon: <TeamOutlined />,
                label: <Link to="/administracion-alumnos">Alumnos</Link>,
              },
              {
                key: '/solicitudes-alumnos',
                icon: <FormOutlined />,
                label: <Link to="/solicitudes-alumnos">Solicitud de alumnos</Link>,
              },
              {
                key: '/registro-pagos',
                icon: <DollarOutlined />,
                label: <Link to="/registro-pagos">Pagos</Link>,
              },
            ],
          },
          {
            key: '/solicitudes-clase-alumno',
            icon: <FormOutlined />,
            label: <Link to="/solicitudes-clase-alumno">Solicitud Clase</Link>,
          },
          {
            key: '/administracion-canchas',
            icon: <EnvironmentOutlined />,
            label: <Link to="/administracion-canchas">Canchas</Link>,
          },
          {
            key: '/administracion-categorias',
            icon: <TagsOutlined />,
            label: <Link to="/administracion-categorias">Categorías</Link>,
          },
          {
            key: '/administracion-planes',
            icon: <FileTextOutlined />,
            label: <Link to="/administracion-planes">Planes</Link>,
          },
          {
            key: '/administracion-profesores',
            icon: <UserSwitchOutlined />,
            label: <Link to="/administracion-profesores">Profesores</Link>,
          },
          {
            key: '/clases-por-profesor',
            icon: <ScheduleOutlined />,
            label: <Link to="/clases-por-profesor">Clases por profesor</Link>,
          },
          {
            key: '/asistencia-bot',
            icon: <CheckCircleOutlined />,
            label: <Link to="/asistencia-bot">Confirmación Clases</Link>,
          },
          modulosMenuItem,
        ];
      }
      case 'ALUMNO':
        return [
          {
            key: '/home-alumno',
            icon: <HomeOutlined />,
            label: <Link to="/home-alumno">Inicio</Link>,
          },
          {
            key: '/perfil',
            icon: <UserOutlined />,
            label: <Link to="/perfil">Mis datos</Link>,
          },
          {
            key: '/mi-historial-pagos',
            icon: <DollarOutlined />,
            label: <Link to="/mi-historial-pagos">Mis compras</Link>,
          },
          {
            key: '/mis-clases',
            icon: <CalendarOutlined />,
            label: <Link to="/mis-clases">Mis Clases</Link>,
          },
          {
            key: '/solicitud-clase',
            icon: <FormOutlined />,
            label: <Link to="/solicitud-clase">Solicitud Clase</Link>,
          },
          ...(isModuleEnabled('club')
            ? [
                {
                  key: '/reservar-cancha',
                  icon: <EnvironmentOutlined />,
                  label: <Link to="/reservar-cancha">Reservar Cancha</Link>,
                },
                {
                  key: '/mis-turnos-cancha',
                  icon: <CalendarOutlined />,
                  label: <Link to="/mis-turnos-cancha">Mis Turnos</Link>,
                },
              ]
            : []),
        ];
      case 'JUGADOR':
        return [
          ...(isModuleEnabled('club')
            ? [
                {
                  key: '/jugador/nuevo-turno',
                  icon: <CalendarOutlined />,
                  label: <Link to="/jugador/nuevo-turno">Nuevo Turno</Link>,
                },
                {
                  key: '/jugador/mis-turnos',
                  icon: <CalendarOutlined />,
                  label: <Link to="/jugador/mis-turnos">Mis Turnos</Link>,
                },
              ]
            : []),
          ...(isModuleEnabled('torneos')
            ? [
                {
                  key: '/jugador/mis-torneos',
                  icon: <TrophyOutlined />,
                  label: <Link to="/jugador/mis-torneos">Mis Torneos</Link>,
                },
              ]
            : []),
          {
            key: '/jugador/mis-datos',
            icon: <UserOutlined />,
            label: <Link to="/jugador/mis-datos">Mis datos</Link>,
          },
        ];
      default:
        return [
          {
            key: '/home',
            icon: <HomeOutlined />,
            label: <Link to="/home">Inicio</Link>,
          },
        ];
    }
  };

  const menuItems = getMenuItems();

  // Determinar la clave seleccionada basada en la ruta actual
  const getSelectedKeys = () => {
    const path = location.pathname;
    
    // Buscar en items principales y en children
    for (const item of menuItems) {
      if (item.key === path) return [item.key];
      
      // Buscar en children si existen
      if (item.children) {
        const childMatch = item.children.find(child => child.key === path);
        if (childMatch) return [childMatch.key];
      }
    }
    
    return [];
  };

  // Determinar qué submenús deben estar abiertos
  const getOpenKeys = () => {
    const path = location.pathname;
    const openKeys = [];
    
    for (const item of menuItems) {
      if (item.children) {
        const childMatch = item.children.find(child => child.key === path);
        if (childMatch) {
          openKeys.push(item.key);
        }
      }
    }
    
    return openKeys;
  };

  const selectedKeys = getSelectedKeys();
  const [openKeys, setOpenKeys] = useState(getOpenKeys());

  // Actualizar openKeys cuando cambia la ruta
  useEffect(() => {
    setOpenKeys(getOpenKeys());
  }, [location.pathname]);

  const handleSidebarCollapsed = (collapsed) => {
    setSidebarCollapsed(collapsed);
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  };

  const handleSidebarMouseEnter = () => {
    if (!isAdmin) return;
    if (sidebarHoverTimer.current) clearTimeout(sidebarHoverTimer.current);
    setSidebarCollapsed(false);
  };

  const handleSidebarMouseLeave = () => {
    if (!isAdmin) return;
    sidebarHoverTimer.current = setTimeout(() => {
      setSidebarCollapsed(true);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (sidebarHoverTimer.current) clearTimeout(sidebarHoverTimer.current);
    };
  }, []);

  // Renderizar avatar del usuario
  const renderUserMenu = () => {
    const userRole = user?.tiu_descripcion || '';
    const userTipoUsuario = user?.usu_tipousuario;
    let roleToCheck = userRole.toUpperCase();
    if (!roleToCheck && userTipoUsuario === 1) {
      roleToCheck = 'ADMINISTRADOR';
    }
    if (!roleToCheck && userTipoUsuario === 3) {
      roleToCheck = 'JUGADOR';
    }

    const userMenuItems = [{
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cerrar sesión',
      danger: true,
    }];

    return (
      <Dropdown
        menu={{
          items: userMenuItems,
          onClick: ({ key }) => {
            if (key === 'logout') {
              handleLogout();
            }
          },
        }}
        placement="bottomRight"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <Avatar src={avatarSrc || undefined} icon={!avatarSrc ? <UserOutlined /> : undefined} />
          <Text style={{ color: '#fff', display: isMobile ? 'none' : 'block' }}>
            {user?.usu_usuario || 'Usuario'}
          </Text>
        </div>
      </Dropdown>
    );
  };

  // Layout para móvil
  if (isMobile) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Header
          style={{
            background: palette.bgSurface,
            padding: '0 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            height: 64,
            borderBottom: `1px solid ${palette.border}`,
          }}
        >
          <Button
            type="text"
            icon={<MenuOutlined style={{ color: '#fff', fontSize: 20 }} />}
            onClick={() => setMobileOpen(true)}
            style={{ color: '#fff' }}
          />
          <Text style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
            PlayManager
          </Text>
          <div style={{ width: 40 }} /> {/* Espaciador */}
        </Header>

        <Drawer
          title="Menú"
          placement="left"
          onClose={() => setMobileOpen(false)}
          open={mobileOpen}
          bodyStyle={{ padding: 0 }}
        >
          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={(keys) => setOpenKeys(keys)}
            items={menuItems}
            onClick={() => setMobileOpen(false)}
            style={{ borderRight: 0 }}
          />
          <div style={{ padding: 16, borderTop: `1px solid ${palette.border}`, marginTop: 'auto' }}>
            <Button
              type="text"
              danger
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              block
            >
              Cerrar sesión
            </Button>
          </div>
        </Drawer>

        <Content
          style={{
            marginTop: 64,
            padding: 16,
            background: palette.bgBase,
            minHeight: 'calc(100vh - 64px)',
            color: palette.textPrimary,
          }}
        >
          {children}
        </Content>
      </Layout>
    );
  }

  // Administrador: mostrar selector de módulo en la barra superior
  const isAdminDesktop = !isMobile && isAdmin;

  // Layout para desktop: menú vertical a la izquierda (colapsable, se expande al acercarse)
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        theme="dark"
        collapsible
        collapsed={sidebarCollapsed}
        onCollapse={handleSidebarCollapsed}
        onMouseEnter={handleSidebarMouseEnter}
        onMouseLeave={handleSidebarMouseLeave}
        width={240}
        collapsedWidth={72}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 1001,
          overflow: 'auto',
          transition: 'all 0.2s',
          backgroundColor: palette.bgSurface,
        }}
        trigger={null}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            padding: sidebarCollapsed ? 0 : '0 16px',
            borderBottom: `1px solid ${palette.border}`,
            backgroundColor: palette.bgSurface,
          }}
        >
          {sidebarCollapsed ? (
            <img
              src={playmanagerLogo}
              alt="PM"
              style={{ width: 68, height: 68, objectFit: 'contain' }}
            />
          ) : (
            <img
              src={playmanagerLogo}
              alt="PlayManager"
              style={{ width: 150, height: 42, objectFit: 'contain' }}
            />
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={sidebarCollapsed ? [] : openKeys}
          onOpenChange={setOpenKeys}
          items={menuItems}
          style={{ marginTop: 8, borderRight: 0 }}
          inlineCollapsed={sidebarCollapsed}
        />
      </Sider>

      <Layout style={{ marginLeft: sidebarCollapsed ? 72 : 240, transition: 'margin-left 0.2s', minHeight: '100vh' }}>
        <Header
          style={{
            background: palette.bgSurface,
            padding: '0 16px 0 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            height: 64,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              type="text"
              icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => handleSidebarCollapsed(!sidebarCollapsed)}
              style={{ color: '#fff', fontSize: 18 }}
            />
          </div>
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            {isAdminDesktop && (
              <>
                {/*
                  Mismo ancho para los módulos del header.
                */}
                <Button
                  size="small"
                  type={moduloSeleccionado === 'gestion' ? 'primary' : 'default'}
                  onClick={() => handleSelectModulo('gestion')}
                  disabled={!isModuleEnabled('gestion')}
                  style={{
                    width: 96,
                    filter: isModuleEnabled('gestion') ? 'none' : 'blur(1px)',
                    opacity: isModuleEnabled('gestion') ? 1 : 0.55,
                  }}
                >
                  Gestión
                </Button>
                <Button
                  size="small"
                  type={moduloSeleccionado === 'club' ? 'primary' : 'default'}
                  onClick={() => handleSelectModulo('club')}
                  disabled={!isModuleEnabled('club')}
                  style={{
                    width: 96,
                    filter: isModuleEnabled('club') ? 'none' : 'blur(1px)',
                    opacity: isModuleEnabled('club') ? 1 : 0.55,
                  }}
                >
                  Club
                </Button>
                <Button
                  size="small"
                  type={moduloSeleccionado === 'academia' ? 'primary' : 'default'}
                  onClick={() => handleSelectModulo('academia')}
                  disabled={!isModuleEnabled('academia')}
                  style={{
                    width: 96,
                    filter: isModuleEnabled('academia') ? 'none' : 'blur(1px)',
                    opacity: isModuleEnabled('academia') ? 1 : 0.55,
                  }}
                >
                  Academia
                </Button>
                <Button
                  size="small"
                  type={moduloSeleccionado === 'torneos' ? 'primary' : 'default'}
                  onClick={() => handleSelectModulo('torneos')}
                  disabled={!isModuleEnabled('torneos')}
                  style={{
                    width: 96,
                    filter: isModuleEnabled('torneos') ? 'none' : 'blur(1px)',
                    opacity: isModuleEnabled('torneos') ? 1 : 0.55,
                  }}
                >
                  Torneos
                </Button>
              </>
            )}
            {renderUserMenu()}
          </div>
        </Header>

        <Content
          style={{
            padding: isAdminDesktop ? 24 : 16,
            background: palette.bgBase,
            minHeight: 'calc(100vh - 64px)',
            color: palette.textPrimary,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;

